import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { COLLECTIONS } from '../lib/firebase/collections';
import type { WhiteCard, BlackCard, CardCategory } from '../types/game';
import { motion, AnimatePresence } from 'framer-motion';

const CardManagement: React.FC = () => {
  const [cards, setCards] = useState<{ black: BlackCard[], white: WhiteCard[] }>({ black: [], white: [] });
  const [categories, setCategories] = useState<CardCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const categoriesSnap = await getDocs(collection(db, COLLECTIONS.CARD_CATEGORIES));
      const blackCardsSnap = await getDocs(collection(db, COLLECTIONS.BLACK_CARDS));
      const whiteCardsSnap = await getDocs(collection(db, COLLECTIONS.WHITE_CARDS));

      setCategories(categoriesSnap.docs.map(d => ({ ...d.data(), id: d.id })) as CardCategory[]);
      setCards({
        black: blackCardsSnap.docs.map(d => ({ ...d.data(), id: d.id })) as BlackCard[],
        white: whiteCardsSnap.docs.map(d => ({ ...d.data(), id: d.id })) as WhiteCard[],
      });
    } catch (error) {
      console.error("Fehler beim Laden der Daten:", error);
      alert("Daten konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  const handleDelete = async (cardId: string, cardType: 'black' | 'white') => {
    if (window.confirm('Diese Karte wirklich löschen?')) {
      try {
        const collectionName = cardType === 'black' ? COLLECTIONS.BLACK_CARDS : COLLECTIONS.WHITE_CARDS;
        await deleteDoc(doc(db, collectionName, cardId));
        alert('Karte gelöscht!');
        // Refresh data locally to avoid re-fetching everything
        setCards(prev => ({
          ...prev,
          [cardType]: prev[cardType].filter(card => card.id !== cardId),
        }));
      } catch (error) {
        console.error("Fehler beim Löschen:", error);
        alert("Löschen fehlgeschlagen.");
      }
    }
  };

  const cardsByCategory = useMemo(() => {
    const grouped: { [key: string]: { black: BlackCard[], white: WhiteCard[] } } = {};
    categories.forEach(category => {
      grouped[category.id] = {
        black: cards.black.filter(card => card.categoryIds.includes(category.id)),
        white: cards.white.filter(card => card.categoryIds.includes(category.id)),
      };
    });
    return grouped;
  }, [cards, categories]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-40">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-400"></div>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 p-6 rounded-lg shadow-lg">
      <h2 className="text-2xl font-bold text-white mb-6">Kartenverwaltung</h2>
      <div className="space-y-4">
        {categories.map(category => (
          <div key={category.id} className="bg-gray-700/50 rounded-lg overflow-hidden">
            <button
              onClick={() => setActiveCategory(activeCategory === category.id ? null : category.id)}
              className="w-full text-left p-4 flex justify-between items-center bg-gray-700 hover:bg-gray-600 transition"
            >
              <h3 className="font-bold text-lg text-white">{category.name}</h3>
              <motion.span animate={{ rotate: activeCategory === category.id ? 180 : 0 }}>
                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
              </motion.span>
            </button>
            <AnimatePresence>
              {activeCategory === category.id && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="px-4 pb-4"
                >
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
                    {/* Black Cards Table */}
                    <div>
                      <h4 className="font-semibold text-white mb-2">Schwarze Karten ({cardsByCategory[category.id]?.black.length || 0})</h4>
                      <div className="max-h-96 overflow-y-auto rounded-md bg-black/50 p-2">
                        {cardsByCategory[category.id]?.black.map(card => (
                          <div key={card.id} className="bg-black text-white p-3 rounded-md mb-2 flex justify-between items-center text-sm">
                            <p>{card.text} <span className="text-gray-400">(Pick: {card.pick})</span></p>
                            <button onClick={() => handleDelete(card.id, 'black')} className="text-red-500 hover:text-red-400 text-xs font-bold transition-transform hover:scale-110">Löschen</button>
                          </div>
                        ))}
                      </div>
                    </div>
                    {/* White Cards Table */}
                    <div>
                      <h4 className="font-semibold text-white mb-2">Weiße Karten ({cardsByCategory[category.id]?.white.length || 0})</h4>
                      <div className="max-h-96 overflow-y-auto rounded-md bg-white/10 p-2">
                         {cardsByCategory[category.id]?.white.map(card => (
                          <div key={card.id} className="bg-white text-black p-3 rounded-md mb-2 flex justify-between items-center text-sm">
                            <p>{card.text}</p>
                            <button onClick={() => handleDelete(card.id, 'white')} className="text-red-500 hover:text-red-400 text-xs font-bold transition-transform hover:scale-110">Löschen</button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CardManagement; 